<template>
  <div class="documents-revision-history-container">
    <router-view name="sidebar" @hidden="navigateToRevisionHistory" />
    <document-secure-toolbar />
    <div class="aula-document-history-container page-layout-padding">
      <h1 class="document-title">
        <b-btn variant="link" class="file-document" :to="{ name: 'documents' }">
          {{ $t('document.file_and_document') }}
        </b-btn>
        <span class="breadcumb-spacer">/</span>
        <b-btn variant="link" class="file-document" :to="{ name: 'documentsSecureList' }">
          {{ $t('document.secure_file') }}
        </b-btn>
        <span class="breadcumb-spacer">/</span>
        <b>{{ documentTitle }}</b>
      </h1>
      <ObservingContainer :options="observerOptions">
        <b-table
          id="full-revision-history-data-table"
          :items="revisions"
          :fields="fields"
          :busy="isLoading"
          show-empty
          primary-key="id"
        >
          <template #cell(createdAt)="data">
            {{ formatCreatedDate(data.item.createdAt) }}
          </template>
          <template #cell(description)="data">
            {{ getRevisionChangeTypeText(data.item) }}
          </template>
          <template #cell(attachment)="data">
            <div
              v-if="data.item.isAvailable"
              class="media-data-container"
              @click="onRevisionClick(data.item.id)"
              @keydown.enter="onRevisionClick(data.item.id)"
            >
              <div tabindex="0" class="album-media-data" :aria-label="documentTitle">
                <div class="media-item">
                  <div class="media-thumbnail">
                    <i class="icon-Aula_note" />
                  </div>
                  <div class="media-name">
                    {{ data.item.title }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              {{ $t('document.history_version_not_available') }}
            </div>
          </template>
        </b-table>
        <b-table
          id="compact-revision-history-data-table"
          :items="revisions"
          :fields="compactFields"
          :busy="isLoading"
          show-empty
          primary-key="id"
          thead-class="d-none"
        >
          <template #cell(compact)="data">
            <div class="mb-1">
              <strong>{{ $getText('DOCUMENTS_HISTORY_DATE') }}:</strong>
              {{ formatCreatedDate(data.item.createdAt) }}
            </div>
            <div class="mb-1">
              <strong>{{ $getText('DOCUMENTS_HISTORY_USER') }}:</strong>
              {{ data.item.createdBy }}
            </div>
            <div class="mb-1">
              <strong>{{ $getText('DOCUMENTS_HISTORY_CHANGE_TYPE') }}:</strong>
              {{ getRevisionChangeTypeText(data.item) }}
            </div>
            <div
              v-if="data.item.isAvailable"
              class="media-data-container"
              @click="onRevisionClick(data.item.id)"
              @keydown.enter="onRevisionClick(data.item.id)"
            >
              <div class="album-media-data">
                <div class="media-item">
                  <div class="media-thumbnail">
                    <i class="icon-Aula_note" />
                  </div>
                  <div class="media-name">
                    {{ data.item.title }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </b-table>
        <aula-spinner v-if="isLoading" />
        <ObservedTarget v-if="revisions.length > 0" @onIntersecting="onBottomReached" />
      </ObservingContainer>
    </div>
  </div>
</template>
<script>
import { types } from '../../store/types/types';
import { mapActions } from 'vuex';
import { documentTypes } from '../../../shared/enums/documentTypes';
import { revisionChangeTypeEnum } from '../../../shared/enums/RevisionChangeTypeEnum';
import DocumentSecureToolbar from './DocumentSecureToolbar.vue';
import ObservingContainer from '../../../shared/libs/intersection-observer/components/Container.vue';
import ObservedTarget from '../../../shared/libs/intersection-observer/components/Target.vue';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { useUserProfileStore } from '../../../shared/stores';
import { dateFormatEnum } from '../../../shared/enums/dateFormatEnum';
import { useRevisionTable } from '../../composables/documents/useRevisionTable';

export default {
  setup() {
    const { revisionData, revisions, fields, compactFields } = useRevisionTable();

    return {
      revisionData,
      revisions,
      fields,
      compactFields,
    };
  },
  data: function () {
    return {
      documentTitle: '',
      isLoading: true,
      documentTypes,
      paginationStart: 0,
      pageSize: 20,
      revisionChangeTypeMap: new Map([
        [revisionChangeTypeEnum.CREATED, 'document.revision_created'],
        [revisionChangeTypeEnum.EDITED, 'document.revision_edited'],
        [revisionChangeTypeEnum.DELETED, 'document.revision_deleted'],
        [revisionChangeTypeEnum.EXPORTED, 'document.revision_exported'],
        [revisionChangeTypeEnum.RESTORED, 'document.revision_restored'],
        [revisionChangeTypeEnum.SHARED, 'document.revision_shared'],
        [revisionChangeTypeEnum.UNSHARED, 'document.revision_unshared'],
        [revisionChangeTypeEnum.IMPLICIT_UNSHARED, 'document.revision_implicit_unshared'],
        [revisionChangeTypeEnum.UNLOCKED, 'document.revision_unlocked'],
        [revisionChangeTypeEnum.LOCKED, 'document.revision_locked'],
        [revisionChangeTypeEnum.EDIT_PERMISSION_ADDED, 'document.revision_permission_added'],
        [revisionChangeTypeEnum.EDIT_PERMISSION_REMOVED, 'document.revision_permission_removed'],
        [revisionChangeTypeEnum.SENT_TO_ESDH, 'document.revision_sent_to_esdh'],
        [revisionChangeTypeEnum.JOURNALIZED_TO_ESDH, 'document.revision_journalized_to_esdh'],
        [revisionChangeTypeEnum.ESDH_JOURNALIZATION_FAILED, 'document.revision_esdh_journalization_failed'],
        [revisionChangeTypeEnum.RESENT_TO_ESDH, 'document.revision_resent_to_esdh'],
        [revisionChangeTypeEnum.MARK_FOR_MANUAL_JOURNALIZE, 'document.revision_marked_journalize_manually'],
        [revisionChangeTypeEnum.MANUALLY_JOURNALIZED, 'document.revision_manually_journalized_to_esdh'],
      ]),
    };
  },
  computed: {
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    observerOptions() {
      return { rootMargin: '50%' };
    },
    hasMoreRevisions() {
      if (!this.revisionData) {
        return false;
      }
      return this.revisionData.totalCount > (this.paginationStart + 1) * this.pageSize;
    },
  },
  methods: {
    formatCreatedDate(date) {
      return DateTimeUtil.formatDate(date, dateFormatEnum.LONG_DATE_TIME);
    },
    onBottomReached() {
      if (this.isLoading || !this.hasMoreRevisions) {
        return;
      }
      this.getMoreRevisions();
    },
    getNameAndMeta(person) {
      return this.$getProfileDisplayName(person);
    },
    async getRevisions() {
      const documentId = this.$route.params.documentId;
      const payload = {
        documentId,
        offset: this.pageSize * this.paginationStart,
        limit: this.pageSize,
      };
      this.isLoading = true;
      if (this.paginationStart === 0) {
        await this.loadRevisions(payload);
      } else {
        await this.loadMoreRevisions(payload);
      }
      this.isLoading = false;
    },
    onRevisionClick(revisionId) {
      this.$router.push({
        name: 'revisionHistoryDocument',
        params: { revisionId },
      });
    },
    navigateToRevisionHistory() {
      this.$router.push({ name: 'documentsRevisionHistory' });
    },
    getMoreRevisions() {
      this.paginationStart++;
      this.getRevisions();
    },
    getRevisionChangeTypeText(item) {
      const changedUsers = (item.sharedWith.length > 0 ? item.sharedWith : item.unsharedWith)
        .map(item => this.getNameAndMeta(item))
        .join(', ');
      let changeTypeText = '';

      switch (item.changeType) {
        case revisionChangeTypeEnum.SHARED:
        case revisionChangeTypeEnum.UNSHARED:
        case revisionChangeTypeEnum.IMPLICIT_UNSHARED:
        case revisionChangeTypeEnum.EDIT_PERMISSION_ADDED:
        case revisionChangeTypeEnum.EDIT_PERMISSION_REMOVED:
          changeTypeText = this.$t(this.revisionChangeTypeMap.get(item.changeType), { userName: changedUsers });
          break;
        case revisionChangeTypeEnum.SENT_TO_ESDH:
        case revisionChangeTypeEnum.JOURNALIZED_TO_ESDH:
        case revisionChangeTypeEnum.ESDH_JOURNALIZATION_FAILED:
        case revisionChangeTypeEnum.RESENT_TO_ESDH:
        case revisionChangeTypeEnum.MARK_FOR_MANUAL_JOURNALIZE:
        case revisionChangeTypeEnum.MANUALLY_JOURNALIZED:
          changeTypeText = this.$t(this.revisionChangeTypeMap.get(item.changeType), {
            recipientName: item.recipientName,
            childrenNames: item.childrenNames.join(', '),
          });
          break;
        default:
          changeTypeText = this.$t(this.revisionChangeTypeMap.get(item.changeType));
          break;
      }

      return changeTypeText;
    },
    ...mapActions({
      loadRevisions: types.ACTIONS_GET_DOCUMENT_REVISIONS,
      loadMoreRevisions: types.ACTIONS_GET_MORE_DOCUMENT_REVISIONS,
      loadInternalDocument: types.ACTION_LOAD_INTERNAL_DOCUMENT,
      loadExternalDocument: types.ACTION_LOAD_EXTERNAL_DOCUMENT,
    }),
  },
  created() {
    const id = this.$route.params.documentId;
    const documentType = this.$route.params.documentType;
    const params = { id };
    if (documentType === documentTypes.EXTERNAL) {
      this.loadExternalDocument(params).then(response => {
        this.documentTitle = response.title;
      });
    } else {
      this.loadInternalDocument(params).then(response => {
        this.documentTitle = response.title;
      });
    }
    this.getRevisions();
  },
  components: {
    ObservedTarget,
    ObservingContainer,
    'document-secure-toolbar': DocumentSecureToolbar,
  },
};
</script>
<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
@use '../../../shared/assets/scss/components/documents/_overview' as *;

.document-title {
  flex-wrap: wrap;
}

.aula-document-history-container {
  .media-data-container {
    cursor: pointer;
    @include breakpoint-xl-down() {
      margin-top: 5px;
    }

    .album-media-data {
      background-color: $color-grey-light;
      flex: unset;
      flex-grow: 1;
    }
  }

  @include breakpoint-lg-down() {
    padding: 15px;
    padding-bottom: 50px !important;
  }

  @include breakpoint-sm-down() {
    padding: 10px;
  }

  h1 {
    font-size: 18px;
    font-weight: 400;
    margin-bottom: 40px;
  }

  .breadcrumbs {
    cursor: pointer;
  }

  .breadcumb-spacer {
    padding: 0 10px;
    display: inline-block;
  }
}

#full-revision-history-data-table {
  display: table;

  @include breakpoint-lg-down() {
    display: none !important;
  }

  ::v-deep {
    th {
      background-color: var(--color-grey-dark);
    }

    .creator,
    .date {
      width: 15%;
    }

    .description,
    .version {
      width: 35%;
    }
  }
}

#compact-revision-history-data-table {
  display: none;

  @include breakpoint-lg-down() {
    display: table !important;
  }
}
</style>
